import React, { useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Alert, Box, Button, Typography } from '@mui/material';
import { DNumberField } from '../../../../../../dum';
import {
    EXPENSE_RESOURCES,
    IExpense,
    TAX,
    UNIT_PRICE,
    UNITS,
} from '../expense-form';
import { useCalculateExpenseResourcesTotal } from '../use-calculate-expense-resources-total';
import { useExpenseReceiptTypeIdInputQuery } from '../../../../../../../services';
import { formatFloat, round } from '../../../../../../../helpers';

/**
 * IVA splitter — a LOCAL calculator on the expense "Herramientas" tab.
 *
 * When an expense is transferred without an invoice the supplier hands over a
 * single total. This fills the two fields you would otherwise reach for a
 * calculator to derive: the document IVA and the single resource line's
 * subtotal. It is a temporary convenience, corrected later once the real
 * subtotal/tax are known.
 *
 * The amount field is deliberately NOT a form field — it never reaches the
 * server. Only the button writes into the form (`tax` and the one resource
 * line), and only when there is exactly one resource row, because the subtotal
 * is derived from `units × unit_price` per line and a single row maps cleanly.
 *
 * Formula — the true IVA decomposition of a gross total, so a $6,960 total at
 * 16% splits into $6,000 subtotal + $960 IVA (NOT $6,960 × 16% = $1,113.60,
 * which would tax the gross instead of the net base):
 *   subtotal = amount / (1 + rate)
 *   tax      = amount − subtotal
 */
export function ExpenseIvaSplitter() {
    // Untyped on purpose: setValue writes the dynamic
    // `expense_resources.0.unit_price` path, which a typed context rejects
    // (the template literal widens to every key). Mirrors
    // use-set-first-expense-resource-item.
    const { setValue } = useFormContext();
    const { calculateExpenseResourcesTotal } =
        useCalculateExpenseResourcesTotal();

    const [amountText, setAmountText] = useState('');

    const receiptTypeId = useWatch({ name: 'receipt_type_id' }) as
        | number
        | null;
    const expenseResources = useWatch({
        name: EXPENSE_RESOURCES,
    }) as IExpense['expense_resources'] | undefined;
    const currentTax = useWatch({ name: TAX }) as number | null | undefined;

    const { data } = useExpenseReceiptTypeIdInputQuery();
    const receiptType = useMemo(
        () =>
            (data?.getReceiptTypes ?? []).find(
                (rt) => rt.id === receiptTypeId,
            ),
        [data, receiptTypeId],
    );
    const rate = Number(receiptType?.tax_rate ?? 0);

    const amount = Number(amountText);
    const amountValid =
        amountText.trim() !== '' && Number.isFinite(amount) && amount > 0;
    // Decompose the gross total into its net base and the IVA it already
    // contains: subtotal = amount / (1 + rate), tax = the remainder. Deriving
    // tax as (amount − subtotal) keeps subtotal + tax === amount exactly after
    // rounding, so the two written values always reconcile to the entered total.
    const subtotal = amountValid ? round(amount / (1 + rate)) : 0;
    const tax = amountValid ? round(amount - subtotal) : 0;

    const singleRow = (expenseResources?.length ?? 0) === 1;

    // A receipt type with a 0% rate has nothing to split — tax would be 0 and
    // subtotal would just echo the total.
    const hasTax = rate > 0;

    // Nothing to do if the form already holds exactly what the button would
    // write: the same document tax and the same single-line subtotal
    // (units × unit_price, which is what handleFill sets). Compared on rounded
    // values so float noise doesn't keep the button live.
    const firstRow = expenseResources?.[0];
    const currentSubtotal =
        singleRow && firstRow
            ? round(Number(firstRow.units ?? 0) * Number(firstRow.unit_price ?? 0))
            : null;
    const alreadyMatches =
        amountValid &&
        singleRow &&
        hasTax &&
        round(Number(currentTax ?? 0)) === tax &&
        currentSubtotal === subtotal;

    // The amount field is only useful when the tool can act at all; the
    // structural preconditions (one line, a taxed receipt type) gate the input,
    // while amount-dependent reasons (empty, already matching) only gate the
    // button — you have to type to know those.
    const canEnterAmount = singleRow && hasTax;
    const canFill = canEnterAmount && amountValid && !alreadyMatches;

    const handleFill = () => {
        if (!canFill) return;
        // One row, so the subtotal maps to that line: units = 1, unit_price =
        // subtotal. calculateExpenseResourcesTotal then re-derives the document
        // SUBTOTAL from the lines and cascades total_with_tax (which folds in
        // the tax we set just below).
        setValue(TAX, tax, { shouldDirty: true, shouldValidate: true });
        setValue(`${EXPENSE_RESOURCES}.0.${UNITS}`, 1, { shouldDirty: true });
        setValue(`${EXPENSE_RESOURCES}.0.${UNIT_PRICE}`, subtotal, {
            shouldDirty: true,
        });
        calculateExpenseResourcesTotal();
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Typography variant={'subtitle2'}>
                Dividir un total en IVA y subtotal
            </Typography>
            <Typography variant={'caption'} color={'text.secondary'}>
                Escribe el total que te dieron (sin factura). Calcula el IVA con
                la tasa del tipo de comprobante seleccionado y rellena el IVA y
                el subtotal de la única línea de recurso. Es un cálculo temporal;
                corrige el subtotal y el IVA reales después.
            </Typography>

            <DNumberField
                label={'Total (con IVA)'}
                value={amountText}
                onChange={(value) => setAmountText(value)}
                disabled={!canEnterAmount}
                disableGutters
            />

            <Box sx={{ display: 'flex', gap: 3 }}>
                <Box>
                    <Typography variant={'caption'} color={'text.secondary'}>
                        IVA ({formatFloat(rate * 100, 2)}%)
                    </Typography>
                    <Typography variant={'body2'}>
                        {amountValid ? formatFloat(tax) : '—'}
                    </Typography>
                </Box>
                <Box>
                    <Typography variant={'caption'} color={'text.secondary'}>
                        Subtotal
                    </Typography>
                    <Typography variant={'body2'}>
                        {amountValid ? formatFloat(subtotal) : '—'}
                    </Typography>
                </Box>
            </Box>

            {!singleRow ? (
                <Alert severity={'info'}>
                    Disponible sólo cuando el gasto tiene exactamente una línea
                    de recurso (hay {expenseResources?.length ?? 0}).
                </Alert>
            ) : receiptType && !hasTax ? (
                <Alert severity={'info'}>
                    El tipo de comprobante seleccionado no aplica IVA (tasa 0%).
                </Alert>
            ) : alreadyMatches ? (
                <Alert severity={'info'}>
                    El IVA y el subtotal ya coinciden con el cálculo.
                </Alert>
            ) : null}

            <Box>
                <Button
                    variant={'contained'}
                    size={'small'}
                    disabled={!canFill}
                    onClick={handleFill}
                >
                    Rellenar IVA y subtotal
                </Button>
            </Box>
        </Box>
    );
}
